import { memo, useMemo } from 'react';
import _ from 'lodash';
import { motion } from 'framer-motion';
import clsx from 'clsx';

const iconsDefaults = () => ({
  default:
    'M1.008,0.5C0.438,0.583,0.48,1.27,0.521,1.958 c0,169.668,0,339.31,0,508.974c169.364,1.135,340.808,0.162,510.979,0.486c0-170.309,0-340.61,0-510.918 C341.342,0.5,171.167,0.5,1.008,0.5z M259.893,452.167c-11.822,11.919-30.478,18.938-53.429,18.938 c-37.643,0-58.543-18.34-71.884-43.711c12.842-8.2,25.966-16.122,39.344-23.795c5.456,15.262,23.886,32.42,44.683,21.857 c13.183-6.699,11.661-27.01,11.661-49.054c0-45.773,0-98.578,0-139.872c-0.042-0.688-0.083-1.375,0.482-1.458 c15.707,0,31.413,0,47.116,0c0,36.788,0,78.402,0,117.529C277.866,395.199,280.91,430.988,259.893,452.167z M470.696,409.917 c-2.674,39.884-35.243,61.063-79.17,61.188c-43.062,0.124-70.624-19.013-87.433-48.567c12.085-8.317,25.778-15.017,38.375-22.822 c10.08,15.761,27.537,30.91,53.429,28.652c16.131-1.406,34.856-14.555,24.285-34.482c-5.127-9.66-17.516-14.567-28.656-19.425 c-35.352-15.424-76.828-29.571-72.861-84.992c1.327-18.514,9.852-31.525,20.889-40.796c11.311-9.5,26.46-15.867,46.629-16.511 c36.629-1.173,56.723,15.12,70.429,37.884c-11.664,8.891-24.514,16.608-37.401,24.281c-4.229-12.995-24.644-25.658-41.772-17.969 c-7.789,3.493-14.788,13.761-10.684,26.224c3.66,11.115,18.589,17.199,30.599,22.344 C433.706,340.486,474.331,355.693,470.696,409.917z',
});

/**
 * Componente SVG animado que dibuja un ícono usando **Framer Motion**.
 * Permite seleccionar distintos íconos, animar el trazo SVG y personalizar
 * colores, duración y repetición de la animación.
 *
 * @component
 *
 * @example
 * // Ícono por defecto con animación básica
 * <MotionVector />
 *
 * @example
 * // Ícono 'default' con animación infinita y colores personalizados
 * <MotionVector
 *   select="default"
 *   repeat={true}
 *   strokeColor="#FF0000"
 *   fillColor="rgba(255, 0, 0, 0.1)"
 *   duration={2}
 * />
 *
 * @param {Object} props - Propiedades del componente.
 * @param {'default' | string} [props.select='default'] - Clave del ícono en la biblioteca a renderizar.
 * @param {string} [props.className] - Clases CSS adicionales para el contenedor SVG.
 * @param {boolean} [props.repeat=false] - Si la animación debe repetirse indefinidamente.
 * @param {string} [props.fillColor='transparent'] - Color de relleno del SVG.
 * @param {string} [props.strokeColor='#EEEEEE'] - Color del trazo del SVG.
 * @param {Object} [props.initial] - Estado inicial de la animación de la ruta (`motion.path`).
 *   Por defecto: `{ pathLength: 0, opacity: 0.6 }`.
 * @param {Object} [props.final] - Estado final de la animación de la ruta.
 *   Por defecto: `{ pathLength: 1, opacity: 1 }`.
 * @param {'loop' | 'mirror' | 'reverse'} [props.transitionType='mirror'] - Tipo de repetición de la animación.
 * @param {number} [props.duration=1.2] - Duración de la animación en segundos.
 * @param {Object} [props.library] - Biblioteca personalizada de íconos para extender o sobrescribir los predeterminados.
 * @param {string} [props.strokeDasharray='0 1'] - Propiedad SVG para definir patrón de guiones en el trazo.
 * @param {number} [props.strokeWidth=0] - Grosor del trazo SVG.
 * @param {string|number} [props.cordX='2'] - Coordenada X para el atributo `viewBox` del SVG.
 * @param {string|number} [props.cordY='2'] - Coordenada Y para el atributo `viewBox` del SVG.
 * @param {...any} [props.props] - Propiedades adicionales que serán aplicadas al contenedor SVG.
 *
 * @throws {Error} Lanza un error si el valor de `select` no es una cadena o no existe en la biblioteca de íconos.
 *
 * @returns {JSX.Element} SVG animado con el ícono seleccionado.
 */

const MotionVector = memo(
  ({
    select = 'default',
    className,
    repeat = false,
    fillColor = 'transparent',
    strokeColor = '#EEEEEE',
    initial,
    final,
    transitionType,
    library,
    strokeDasharray = '0 1',
    duration = 1.2,
    strokeWidth = 0,
    cordX = '2',
    cordY = '2',
    ...props
  }) => {
    const icons = useMemo(() => _.merge({}, iconsDefaults(), library), [library]);

    if (typeof select !== 'string') throw new Error(`Icon type '${select}' is not a valid option`);
    if (!_.has(icons, select)) throw new Error(`Icon type '${select}' doesnt exist`);
    return (
      <motion.svg
        fill={fillColor}
        preserveAspectRatio="xMidYMid meet"
        viewBox={`${cordX} ${cordY} 20 20`}
        xmlns="http://www.w3.org/2000/svg"
        className={clsx('flex justify-center items-center', className)}
        {...props}
      >
        <motion.path
          initial={typeof initial === 'object' ? initial : { pathLength: 0, opacity: 0.6 }}
          animate={typeof final === 'object' ? final : { pathLength: 1, opacity: 1 }}
          transition={{
            duration,
            delay: 0.2,
            ease: 'easeIn',
            repeat: repeat ? Infinity : undefined,
            repeatType: transitionType || 'mirror',
            repeatDelay: 0.2,
          }}
          strokeWidth={strokeWidth}
          stroke={strokeColor}
          d={icons[select]}
        />
      </motion.svg>
    );
  }
);

export default MotionVector;
